/** Matches ChargingJobService.getBloodyLitersSaved: 220 km ~ 44 kWh ~ 20 L fuel. */
export const KWH_PER_220KM = 44;
export const LITERS_PER_220KM = 20;
/** Gasoline combustion CO₂ equivalent (kg per liter). */
export const CO2_KG_PER_LITER_FUEL = 2.31;

export function kwhToLitersSaved(kwh) {
  const n = Number(kwh);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return (n / KWH_PER_220KM) * LITERS_PER_220KM;
}

export function kwhToCo2KgSaved(kwh) {
  return kwhToLitersSaved(kwh) * CO2_KG_PER_LITER_FUEL;
}

/** Reverse of backend getBloodyLitersSaved (global liters → kWh + CO₂). */
export function globalImpactFromLitersSaved(liters) {
  const litersSaved = Math.round(Number(liters)) || 0;
  if (litersSaved <= 0) {
    return { totalKwh: 0, litersSaved: 0, co2KgSaved: 0 };
  }
  const totalKwh = (litersSaved / LITERS_PER_220KM) * KWH_PER_220KM;
  const co2KgSaved = Math.round(litersSaved * CO2_KG_PER_LITER_FUEL);
  return { totalKwh, litersSaved, co2KgSaved };
}

export function getTotalKwhFromHistory(historyData) {
  if (!historyData?.length) return 0;
  return historyData.reduce((sum, item) => sum + (item.energyWth || 0) / 1000, 0);
}

const IMPACT_NUMBER_LOCALE = 'en-US';

export function formatImpactCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return '0';
  }
  return Math.round(n).toLocaleString(IMPACT_NUMBER_LOCALE);
}

/** Display CO₂ kilograms as metric tons (rounded). */
export function formatImpactCo2Tons(co2Kg) {
  const kg = Number(co2Kg);
  if (!Number.isFinite(kg) || kg <= 0) {
    return '0';
  }
  return formatImpactCount(kg / 1000);
}

export function formatHistoryKwh(kwh) {
  const n = Number(kwh);
  if (!Number.isFinite(n) || n <= 0) {
    return '0';
  }
  const rounded = Number(n.toFixed(1));
  const hasFraction = !Number.isInteger(rounded);
  return rounded.toLocaleString(IMPACT_NUMBER_LOCALE, {
    minimumFractionDigits: hasFraction ? 1 : 0,
    maximumFractionDigits: 1,
  });
}

/**
 * 1% SoC on a 200 kWh pack. At or above this energy, a rounded gain of 0% is
 * stuck telemetry (for example 13 kWh reported as +0%).
 */
export const KWH_PER_IMPLIED_SOC_PERCENT = 2;

export function isFlatSocImplausible(energyWth, socDeltaPercent) {
  const kwh = Number(energyWth) / 1000;
  const delta = Number(socDeltaPercent);
  if (!Number.isFinite(kwh) || kwh <= 0 || !Number.isFinite(delta)) {
    return false;
  }
  if (Math.round(delta) > 0) {
    return false;
  }
  return kwh / KWH_PER_IMPLIED_SOC_PERCENT >= 1;
}

export function formatSocSummaryLine(t, startedPercent, chargedPercent, totalPercent) {
  return t('historyPage.summarySocLine', {
    started: Math.round(Number(startedPercent)),
    charged: Math.round(Number(chargedPercent)),
    total: Math.round(Number(totalPercent)),
  });
}

/** Average session power (kW) from delivered energy and duration. */
export function computeSessionAvgPowerKw(energyWth, durationSeconds) {
  const energy = Number(energyWth);
  const duration = Number(durationSeconds);
  if (!Number.isFinite(energy) || !Number.isFinite(duration) || duration <= 0) {
    return null;
  }
  return energy / (duration / 3600) / 1000;
}
